/**
 * EB-2 NIW (National Interest Waiver) Visa Criteria Definitions
 *
 * The EB-2 NIW is for individuals with advanced degrees or exceptional ability
 * whose employment would greatly benefit the United States and who seek to waive
 * the job offer and labor certification requirements.
 *
 * Legal Reference: Matter of Dhanasar, 26 I&N Dec. 884 (AAO 2016)
 */

import { VisaCriterion } from './types';

/**
 * EB-2 NIW visa criteria
 *
 * The beneficiary must satisfy all three prongs of the Dhanasar test:
 * 1. The proposed endeavor has substantial merit and national importance
 * 2. The individual is well positioned to advance the proposed endeavor
 * 3. On balance, it would be beneficial to the United States to waive the
 *    requirements of a job offer and labor certification
 */
export const EB2NIW_CRITERIA: VisaCriterion[] = [
  {
    id: 'eb2niw_substantial_merit',
    name: 'Substantial Merit',
    description: 'The proposed endeavor has substantial merit and national importance. This can be demonstrated through evidence that the work has significant potential value to the U.S. economy, culture, education, healthcare, or other national interests',
    weight: 9,
    requiredDocuments: [
      'RESEARCH_PAPER',
      'PROJECT_DOCUMENTS',
      'IMPACT_ANALYSIS'
    ],
    legalReference: 'Matter of Dhanasar (2016)',
    minimumEvidence: 3
  },
  {
    id: 'eb2niw_well_positioned',
    name: 'Well Positioned to Advance',
    description: 'You are well positioned to advance the proposed endeavor. This includes consideration of your education, skills, knowledge, track record of success in related efforts, and a plan to continue the work in the United States',
    weight: 9,
    requiredDocuments: [
      'RESUME_CV',
      'RECOMMENDATION_LETTER'
    ],
    legalReference: 'Matter of Dhanasar (2016)',
    minimumEvidence: 2
  },
  {
    id: 'eb2niw_benefit_outweighs',
    name: 'Benefit Outweighs Labor Certification',
    description: 'On balance, it would be beneficial to the United States to waive the requirements of a job offer and thus of a labor certification. This considers factors such as the urgency of the endeavor, potential benefits, and whether requiring a job offer would be impractical',
    weight: 8,
    requiredDocuments: [
      'GOVERNMENT_COLLABORATION_PROOF',
      'INDUSTRY_CONTRIBUTION_REPORT'
    ],
    legalReference: 'Matter of Dhanasar (2016)',
    minimumEvidence: 2
  }
];

/**
 * Get EB-2 NIW criteria configuration
 */
export const EB2NIW_CONFIG = {
  visaType: 'EB-2 NIW',
  criteria: EB2NIW_CRITERIA
};
